import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import MyPhoto from './AnspaughHeadshot2.png';

const PageContainer = styled.div`
  width: 100%;
  background: #ffffff;
`;

const HeroSection = styled.section`
  padding: 80px 100px 60px;
  background: linear-gradient(135deg, #453793 0%, #5a4bb5 100%);
  color: white;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  h1 {
    font-size: 42px;
    font-weight: 700;
    margin-bottom: 20px;
    color: white;
  }

  p {
    font-size: 20px;
    max-width: 700px;
    margin: 0 auto;
    color: rgba(255, 255, 255, 0.9);
    word-break: break-word;
  }

  @media (max-width: 768px) {
    padding: 40px 24px;
    h1 { font-size: 28px; }
    p { font-size: 16px; }
  }
`;

const AboutSection = styled.section`
  padding: 80px 100px;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 60px;
  align-items: flex-start;

  @media (max-width: 968px) {
    flex-direction: column-reverse;
    align-items: center;
    padding: 60px 40px;
    gap: 40px;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const AboutContent = styled.div`
  flex: 1;

  h2 {
    font-size: 32px;
    color: #453793;
    margin-bottom: 25px;
    font-weight: 700;
  }

  h3 {
    font-size: 24px;
    color: #333;
    margin-top: 35px;
    margin-bottom: 15px;
    font-weight: 600;
  }

  p {
    font-size: 17px;
    line-height: 1.8;
    color: #555;
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 20px 0;
  }

  li {
    padding: 8px 0;
    padding-left: 25px;
    position: relative;
    color: #555;
    line-height: 1.6;
    font-size: 16px;

    &:before {
      content: "✓";
      position: absolute;
      left: 0;
      color: #8ecf41;
      font-weight: bold;
      font-size: 18px;
    }
  }

  @media (max-width: 968px) {
    text-align: center;

    ul {
      text-align: left;
      display: inline-block;
    }
  }
`;

const PhotoContainer = styled.div`
  flex: 0 0 350px;

  img {
    width: 100%;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 968px) {
    flex: 0 0 auto;
    max-width: 300px;
  }

  @media (max-width: 768px) {
    max-width: 250px;
  }
`;

const CredentialsSection = styled.div`
  background: #f8f9fa;
  padding: 40px;
  border-radius: 12px;
  margin-top: 40px;

  h3 {
    margin-top: 0 !important;
  }
`;

const BadgesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 20px;

  .badge {
    padding: 10px 20px;
    background: white;
    border-radius: 6px;
    font-size: 14px;
    color: #555;
    font-weight: 500;
    border: 2px solid #e0e0e0;
  }
`;

const AppList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0;
`;

const AppItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  .emoji {
    font-size: 22px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .text {
    font-size: 16px;
    color: #555;
    line-height: 1.5;

    strong {
      color: #453793;
    }
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  margin-top: 30px;
  padding: 15px 40px;
  background-color: #8ecf41;
  color: white;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;

  &:hover {
    background-color: #72b237;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(142, 207, 65, 0.3);
  }

  @media (max-width: 968px) {
    display: block;
    text-align: center;
  }
`;

function About() {
    return (
        <PageContainer>
            {/* HERO */}
            <HeroSection>
                <h1>About</h1>
                <p>20 years building software for other people. Now building apps for real people. Starting with myself.</p>
            </HeroSection>

            {/* MAIN CONTENT */}
            <AboutSection>
                <AboutContent>
                    <h2>Susan Anspaugh, MBA</h2>
                    <p style={{ fontSize: '18px', color: '#8ecf41', fontWeight: '600', marginBottom: '25px' }}>
                        Founder & App Developer
                    </p>

                    <p>
                        I founded Zumosoft in 2020. For most of my career I built enterprise software for other
                        companies: platforms serving thousands of users, migrations affecting entire organizations,
                        systems that had to work. I learned what good software actually takes.
                    </p>

                    <p>
                        Now I'm turning that same experience toward something more personal: building consumer
                        mobile apps that solve real problems. Not enterprise dashboards. Not demos. Apps I actually
                        use. Apps I wish had existed when I needed them.
                    </p>

                    <h3>Why Apps?</h3>
                    <p>
                        I kept running into the same problem: the tool I needed didn't exist, or it existed but was
                        bloated, confusing, or built for someone else. So I built ZumoDO for myself: a task app
                        organized by time of day instead of an impossible flat list. I use it every single day.
                        That's the standard every Zumosoft app has to meet.
                    </p>

                    <h3>The App Lineup</h3>
                    <AppList>
                        <AppItem>
                            <div className="emoji">✅</div>
                            <div className="text"><strong>ZumoDO</strong>:Tasks, goals, habits, and calendar organized by Morning, Afternoon, Evening, and Night. Live on Android. iOS coming late 2026.</div>
                        </AppItem>
                        <AppItem>
                            <div className="emoji">🏠</div>
                            <div className="text"><strong>ZumoPATH</strong>:Home buying navigator with real mortgage credit scores, debt payoff tools, and honest advice. No sales pitch. Coming 2027.</div>
                        </AppItem>
                        <AppItem>
                            <div className="emoji">🥦</div>
                            <div className="text"><strong>EAT 54321</strong>:Simple daily nutrition checklist. 5 vegetables, 4 whole grains, 3 proteins, 2 fruits, 1 nuts or seeds. No calorie math. In development.</div>
                        </AppItem>
                        <AppItem>
                            <div className="emoji">🤝</div>
                            <div className="text"><strong>ZumoGIVE</strong>:Connecting charities to the skills, services, and people they actually need. Community matchmaking for good. Coming 2027.</div>
                        </AppItem>
                        <AppItem>
                            <div className="emoji">📚</div>
                            <div className="text"><strong>One more thing...</strong> We're not ready to talk about this one yet. It's nothing like our other apps. Coming 2027.</div>
                        </AppItem>
                    </AppList>

                    <h3>The Background That Makes It Work</h3>
                    <ul>
                        <li>Solo-architected MedExplain: React, Cosmos DB, Azure, AI translation, 2,000+ daily users</li>
                        <li>Led enterprise teams of 10–15 engineers at a $4B company</li>
                        <li>Ilitch Holdings IT Impact Award for the 5,000-user M365 migration across 8 companies</li>
                        <li>Only Manager presenting quarterly to CEO at Ilitch Holdings</li>
                        <li>Delivered 30% cost savings and 70% velocity improvements consistently</li>
                        <li>HIPAA-ready architecture and clinical workflow experience</li>
                        <li>WOSB (Women-Owned Small Business) certification in progress</li>
                    </ul>

                    <CredentialsSection>
                        <h3>Education & Certifications</h3>
                        <ul style={{ marginTop: '15px', marginBottom: '15px' }}>
                            <li>MBA, Seattle University (2024)</li>
                            <li>Executive Leadership Certificate, Albers School of Business and Economics at Seattle University (2024)</li>
                            <li>Women's Entrepreneurship Certificate, eCornell (2021)</li>
                            <li>Dale Carnegie Training, Strictly Business Immersion Seminar (2018)</li>
                            <li>WOSB (Women-Owned Small Business) Certification Pending</li>
                        </ul>

                        <BadgesGrid>
                            <div className="badge">React Native</div>
                            <div className="badge">React</div>
                            <div className="badge">TypeScript</div>
                            <div className="badge">Node.js</div>
                            <div className="badge">Azure</div>
                            <div className="badge">Cosmos DB</div>
                            <div className="badge">Azure OpenAI</div>
                            <div className="badge">Firebase Auth</div>
                            <div className="badge">M365</div>
                            <div className="badge">HIPAA Compliance</div>
                        </BadgesGrid>
                    </CredentialsSection>

                    <CTAButton
                        href="https://play.google.com/store/apps/details?id=com.zumodo"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Try ZumoDO Free for 14 Days
                    </CTAButton>
                    <CTAButton as={Link} to="/zumodo" style={{ marginLeft: '15px', backgroundColor: 'transparent', border: '2px solid #8ecf41', color: '#8ecf41' }}>
                        🍎 Join iOS Waitlist
                    </CTAButton>
                </AboutContent>

                <PhotoContainer>
                    <img src={MyPhoto} alt="Susan Anspaugh, MBA, Founder of Zumosoft" />
                </PhotoContainer>
            </AboutSection>
        </PageContainer>
    );
}

export default About;
